import { Container, Grid } from '@mui/material';
import { Paths, ProjectPathParams } from '../../../app/router/app.router';
import { PermissionBoundary } from '../../../core/permissions/permission-boundary.component';
import { Permissions } from '../../../core/permissions/permissions.typings';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { EntityHistoryTimelineCard } from '../../../ui/components/pure/entity-history-timeline/entity-history-timeline-card/entity-history-timeline-card.component';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { NotesCard } from '../../../ui/components/pure/notes-card/notes-card.component';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { CourierDetailsPageHeader } from '../../../ui/components/pure/page-headers/details/courier-details/courier-details-page-header.component';
import { UserDeliveriesList } from '../../users/details/components/user-deliveries-list/user-deliveries-list.component';
import { CourierDetailsActions } from './actions/courier-details.actions';
import { CourierDetailsActionsObserver } from './courier-details-actions-observer.component';
import { useCourierDetails } from './hooks/use-courier-details.hook';
import { useCourierEntityHistory } from './hooks/use-courier-entity-history.hook';
import { useCourierNotes } from './hooks/use-courier-notes.hook';
import { useCourierDetailsStyles } from './courier-details.styles';
import { makeCourierDetailsHeaderProps } from './logic/courier-details-props.map';

/**
 * A courier's details. They used to resolve through the user details page, whose project comes from
 * the users endpoint — the wrong one for a courier, and one their carrier's admins cannot read.
 */
export function CourierDetailsPage({ params }: { params: ProjectPathParams<Paths.courier> }) {
  const {
    courier,
    canDecideApproval,
    breadcrumbAccess,
    capabilities,
    registrationHub,
    deliveries,
    deliveriesInfiniteScroll,
    formState,
    onFormInput,
    actions
  } = useCourierDetails(params.courierID, params.projectID);
  const notesProps = useCourierNotes(params.courierID, params.projectID);
  const entityHistory = useCourierEntityHistory(params.courierID);
  const classes = useCourierDetailsStyles();

  if (courier.error) {
    if (courier.error.statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if (courier.error.statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" entity="courier" />;
    }

    if (courier.error.statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" entity="courier" />;
    }

    return <ErrorFullPage />;
  }

  if (courier.isLoading) {
    return <LoadingIndicator fullPage />;
  }

  if (!courier.data) {
    return <ErrorFullPage preset="NOT_FOUND" entity="courier" />;
  }

  const headerProps = makeCourierDetailsHeaderProps(
    courier.data,
    params.projectID,
    actions.setAction,
    capabilities.data,
    canDecideApproval,
    registrationHub.data,
    breadcrumbAccess
  );

  return (
    <Container>
      <WindowTitle detail={courier.data.displayName || 'Loading'} pageTitle="Couriers" />
      <CourierDetailsPageHeader {...headerProps} />
      {/* The project here only scopes the edit modal's mobile-number formatter to a country, so it
          asks the project the viewer reached this page through rather than the courier's own — a
          region's admins cannot read the carrier project their couriers belong to. */}
      <CourierDetailsActionsObserver actions={actions} courier={courier.data} projectID={params.projectID} />

      <Grid container spacing={1} style={{ display: 'flex' }}>
        <Grid
          className={classes.deliveries}
          size={{
            xs: 12,
            lg: 9
          }}
        >
          <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
            <UserDeliveriesList
              params={params}
              pages={deliveries.data?.pages || []}
              infiniteScroll={deliveriesInfiniteScroll}
              isLoading={deliveries.isLoading}
              isPreviousData={deliveries.isPreviousData}
              formState={formState}
              onFormInput={onFormInput}
              hasError={deliveries.isError}
              isCourier
              totalRecords={deliveries.data?.pages[0]?.totalRecords}
            />
          </PermissionBoundary>
        </Grid>
        <Grid
          className={classes.sideColumn}
          size={{
            xs: 12,
            lg: 3
          }}
        >
          <NotesCard
            onClick={notesProps.handlers.onLoadMoreNotes}
            onAddNote={() => actions.setAction(CourierDetailsActions.ADD_NOTE)}
            {...notesProps}
          />
          <EntityHistoryTimelineCard
            {...entityHistory}
            currentProjectID={params.projectID}
            permission={Permissions.users.get}
            className={classes.historyCard}
          />
        </Grid>
      </Grid>
    </Container>
  );
}
